import { importWithOneRetry } from './dynamicImportRetry';
import { drainEarlyErrorBuffer, type EarlyErrorEntry } from './earlyErrorBuffer';
import { scrubSentryBreadcrumb, scrubSentryEvent } from './sentryScrub';

const MAX_SENTRY_QUEUE_ITEMS = 500;

type SentryModule = typeof import('@sentry/react');

type QueueSetUser = {
  kind: 'setUser';
  user: { id: string } | null;
};

type QueueCapture = {
  kind: 'captureException';
  error: Error;
  extra?: Record<string, unknown>;
};

type SentryQueueItem = QueueSetUser | QueueCapture;

const queue: SentryQueueItem[] = [];
let sentryModule: SentryModule | null = null;
let initPromise: Promise<SentryModule | null> | null = null;

function reasonToError(reason: unknown): Error {
  if (reason instanceof Error) return reason;
  if (typeof reason === 'string') return new Error(reason);
  try {
    return new Error(JSON.stringify(reason));
  } catch {
    return new Error('Unhandled rejection');
  }
}

function flushEarlyErrors(Sentry: SentryModule): void {
  const entries = drainEarlyErrorBuffer();
  for (const entry of entries) {
    if (entry.kind === 'error') {
      const error = new Error(entry.message);
      if (entry.stack) error.stack = entry.stack;
      Sentry.captureException(error, {
        extra: {
          earlyBuffer: true,
          filename: entry.filename,
          lineno: entry.lineno,
          colno: entry.colno,
          bufferedAt: entry.ts,
        },
      });
    } else {
      Sentry.captureException(reasonToError(entry.reason), {
        extra: { earlyBuffer: true, bufferedAt: entry.ts },
      });
    }
  }
}

function trimSentryQueueIfNeeded(): void {
  while (queue.length > MAX_SENTRY_QUEUE_ITEMS) {
    queue.shift();
  }
}

function replayQueue(Sentry: SentryModule): void {
  for (const item of queue) {
    if (item.kind === 'setUser') {
      Sentry.setUser(item.user);
    } else {
      Sentry.captureException(item.error, {
        extra: item.extra,
      });
    }
  }
  queue.length = 0;
}

export async function initializeSentry(): Promise<SentryModule | null> {
  if (!process.env.REACT_APP_SENTRY_DSN) return null;
  if (sentryModule) return sentryModule;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const loaded = await importWithOneRetry(() => import('@sentry/react'));
    if (!loaded) return null;
    const Sentry = loaded;
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeSend: (event) =>
        scrubSentryEvent(
          event as unknown as Parameters<typeof scrubSentryEvent>[0],
        ) as unknown as typeof event,
      beforeBreadcrumb: (breadcrumb) =>
        scrubSentryBreadcrumb(
          breadcrumb as unknown as Parameters<typeof scrubSentryBreadcrumb>[0],
        ) as unknown as typeof breadcrumb,
      integrations: (defaults) =>
        defaults.filter(
          (integration) =>
            integration.name !== 'BrowserTracing' &&
            integration.name !== 'Replay' &&
            integration.name !== 'BrowserSessionReplay',
        ),
    });
    sentryModule = Sentry;
    flushEarlyErrors(Sentry);
    replayQueue(Sentry);
    return Sentry;
  })().catch(() => null);

  return initPromise;
}

export function resetSentryClientForTests(): void {
  queue.length = 0;
  sentryModule = null;
  initPromise = null;
}

function setUser(user: { id: string } | null): void {
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  if (sentryModule) {
    sentryModule.setUser(user);
    return;
  }
  queue.push({ kind: 'setUser', user });
  trimSentryQueueIfNeeded();
}

function captureException(
  error: Error,
  context?: { extra?: Record<string, unknown> },
): void {
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  if (sentryModule) {
    sentryModule.captureException(error, context);
    return;
  }
  queue.push({
    kind: 'captureException',
    error,
    extra: context?.extra,
  });
  trimSentryQueueIfNeeded();
}

export const sentryClient = {
  setUser,
  captureException,
};

export function getSentryQueueForTests(): readonly SentryQueueItem[] {
  return queue;
}

export function flushEarlyErrorsForTests(entries: EarlyErrorEntry[]): void {
  if (!sentryModule) return;
  drainEarlyErrorBuffer();
  for (const entry of entries) {
    if (entry.kind === 'error') {
      sentryClient.captureException(new Error(entry.message));
    } else {
      sentryClient.captureException(reasonToError(entry.reason));
    }
  }
}
